/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { computed, observable, action } from 'mobx'
import { injectable } from 'inversify'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IBatchesStateManager, IRunDateDropdown, IOwnerUser, ITransactionStatus, ICoverageDiscoveryBatchHistoryGridCriteria, ICoverageDiscoveryBatchHistoryGridResults } from './contracts'
import { ValidationResult } from '../../arc/utils/ValidationResult/ValidationResult'
import { IBatchesApiClient } from '../BatchesApiClient/contracts'
import { IExportRequestApiClient, IExportRequest } from '../ExportRequestApiClient/contracts'
import { ITransactionStatusApiClient } from '../TransactionStatusApiClient/contracts'

@injectable()
export class BatchesStateManager implements IBatchesStateManager {
	@observable
	isDataLoadingForInterval: boolean

	@observable
	intervalId: number | null

	@observable
	selectedOwnerIds: Set<number>

	@observable
	selectedTransactionStatusIds: Set<number>

	@observable
	runDateDropdownList: IRunDateDropdown[]

	@observable
	checkExportRequestSubmission: boolean

	@observable
	batchExportRequestForJob: IExportRequest

	@observable
	availableOwners: IOwnerUser[]

	@observable
	availableTransactionStatuses: ITransactionStatus[]

	@observable
	batchHistoryGridCriteria: ICoverageDiscoveryBatchHistoryGridCriteria

	@observable
	batchHistoryGridResults: ICoverageDiscoveryBatchHistoryGridResults

	@injectProperty('IBatchesApiClient')
	public BatchesApiClient!: IBatchesApiClient

	@injectProperty('IExportRequestApiClient')
	public ExportRequestApiClient!: IExportRequestApiClient

	@injectProperty('ITransactionStatusApiClient')
	public TransactionStatusApiClient!: ITransactionStatusApiClient

	constructor() {
		this.availableOwners = []
		this.runDateDropdownList = []
		this.availableTransactionStatuses = []
		this.batchHistoryGridCriteria = {
			searchTerm: {
				ownerUsers: [],
				transactionStatuses: [],
			},
			dateRange: 0,
			page: 1,
			sizePerPage: 25,
			sortField: 'batchId',
			sortOrder: 'desc',
		}
		this.batchHistoryGridResults = {
			items: [],
			total: 0,
		}
		this.batchExportRequestForJob = {
			transactionIds: [],
			batchRunId: 0,
		}
		this.checkExportRequestSubmission = false
		this.selectedOwnerIds = new Set()
		this.selectedTransactionStatusIds = new Set()
		this.intervalId = null
		this.isDataLoadingForInterval = false
	}

	@computed get validateSelectedRequestDetail(): ValidationResult {
		const result = new ValidationResult()
		return result
	}

	@action getTransactionReport(request: IExportRequest): Promise<any> {
		return this.ExportRequestApiClient.getTransactionReport(request)
	}

	@action loadBatchHistoryGridResults(): Promise<void> {
		return this.BatchesApiClient.list(this.batchHistoryGridCriteria)
			.then(response => {
				this.setBatchHistoryGridResults(response)
				this.isDataLoadingForInterval = false
			})
			.catch(error => {
				this.isDataLoadingForInterval = false
				console.log(error)
			})
	}

	@action setBatchHistoryGridResults(results: ICoverageDiscoveryBatchHistoryGridResults): void {
		this.batchHistoryGridResults = results
	}

	@action setBatchHistoryGridCriteria(criteria: ICoverageDiscoveryBatchHistoryGridCriteria, isDataLoadingForInterval: boolean): Promise<void> {
		this.isDataLoadingForInterval = isDataLoadingForInterval
		this.batchHistoryGridCriteria = criteria
		return this.loadBatchHistoryGridResults()
	}

	@action loadAvailableOwners(): Promise<void> {
		return this.BatchesApiClient.getAllBatchOwners()
			.then(response => {
				this.setAvailbleOwners(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the owners')
			})
	}

	@action setAvailbleOwners(availableOwners: IOwnerUser[]): void {
		this.availableOwners = []
		this.availableOwners = availableOwners
	}

	@action setOwnerValue(id: number, selected: boolean): void {
		if (selected) {
			this.selectedOwnerIds.add(id)
		} else {
			this.selectedOwnerIds.delete(id)
		}
	}

	@action loadAvailableTransactionStatuses(): Promise<void> {
		return this.TransactionStatusApiClient.getAllTransactionStatuses()
			.then(response => {
				this.setAvailbleTransactionStatus(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the statuses')
			})
	}

	@action saveBatchRunIdForExportableJobs(fileName: string, batchRunId: number): Promise<void> {
		this.batchExportRequestForJob.fileName = fileName
		this.batchExportRequestForJob.batchRunId = batchRunId
		this.batchExportRequestForJob.transactionIds = []
		return this.ExportRequestApiClient.sendSelectedTransactionIdsForExportJob(this.batchExportRequestForJob)
			.then(() => {
				this.checkExportRequestSubmission = true
			})
			.catch(error => {
				this.checkExportRequestSubmission = false
				console.log(error)
				throw new Error('An error occurred when saving the export request.')
			})
	}

	@action setAvailbleTransactionStatus(availableTransactionStatuses: ITransactionStatus[]): void {
		this.availableTransactionStatuses = []
		this.availableTransactionStatuses = availableTransactionStatuses
	}

	@action setTransactionStatusValue(id: number, selected: boolean): void {
		if (selected) {
			this.selectedTransactionStatusIds.add(id)
		} else {
			this.selectedTransactionStatusIds.delete(id)
		}
	}

	@action deleteSelectedRecord(batchId: number, batchRunId: number): Promise<void> {
		return this.BatchesApiClient.deleteSelectedRecord(batchId, batchRunId).catch(error => {
			console.log(error)
			throw new Error('An error occurred when deleting the batch')
		})
	}

	@action loadRunDatesRange(): void {
		this.runDateDropdownList = []
		this.runDateDropdownList.push(
			{
				id: 0,
				selectionText: '--select/ within--',
			},
			{
				id: 1,
				selectionText: '1 day',
			},
			{
				id: 2,
				selectionText: '3 days',
			},
			{
				id: 3,
				selectionText: '1 week',
			},
			{
				id: 4,
				selectionText: '2 weeks',
			},
			{
				id: 5,
				selectionText: '1 month',
			},
			{
				id: 6,
				selectionText: '6 months',
			},
			{
				id: 7,
				selectionText: '1 year',
			},
		)
	}
}
